class Observer<T = any> {
  private _shouldRemoveAfterFirstRun: boolean = false;
  private _callback: (eventData: T) => unknown;

  constructor(callback: (eventData: T) => unknown, once = false) {
    this._callback = callback;
    this._shouldRemoveAfterFirstRun = once;
  }

  get shouldRemoveAfterFirstRun() {
    return this._shouldRemoveAfterFirstRun;
  }

  get callback() {
    return this._callback;
  }
}

export class Observable<T = any> {
  private observers: Array<Observer<T>> = [];
  private _promise!: Promise<T>;
  private _resolve!: (eventData: T) => void;

  constructor() {
    this.resetPromise();
  }

  add(callback: (eventData: T) => unknown) {
    this.observers.push(new Observer(callback, false));
  }

  addOnce(callback: (eventData: T) => unknown) {
    this.observers.push(new Observer(callback, true));
  }

  notifyObservers(eventData: T) {
    for (const observer of this.observers) {
      observer.callback(eventData);
    }
    this.observers = this.observers.filter((o) => !o.shouldRemoveAfterFirstRun);
    this._resolve(eventData);
    this.resetPromise();
  }

  private resetPromise() {
    this._promise = new Promise((resolve) => {
      this._resolve = (eventData: T) => resolve(eventData);
    });
  }

  then<TResult1 = T, TResult2 = never>(
    onfulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): Promise<TResult1 | TResult2> {
    return this._promise.then(onfulfilled, onrejected);
  }

  catch<TResult = never>(
    onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | null,
  ): Promise<T | TResult> {
    return this._promise.catch(onrejected);
  }

  finally(onfinally?: (() => void) | null): Promise<T> {
    return this._promise.finally(onfinally);
  }
}
